<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：マスタベースクラス
 *
 * 作成者：2021/12/30 ノア）黄
 * 最終修正者：2021/12/30 ノア）黄
 *
 -->
<template>
  <div></div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Component, Vue, Ref, Watch } from 'vue-property-decorator'
import { cloneDeep, findIndex, indexOf, isEmpty } from 'lodash'
import { Form, Table } from 'element-ui'

import { i18n } from '@/locales'
import masterConfig from '@/config/masterConfig'
import { common } from '@/store/modules'
import { getBusinessOfficeMasterData, checklock, lock, unlock as unlockApi, templateDownload } from '@/store/apis'
import { msgBoxService, MessageBoxOption } from '@/commons/services/msgBoxService'
import { MasterData, BusinessOfficeMasterData, LockParam, ResponseData, CommonResponse, SearchTargets } from '@/models'
import HelperService from '@/service/helperService'
import CSVService from '@/service/csvService'
import { validateService } from '@/service/validateService'
import { DataType, MaxFileSize, MaxDataCount, MasterType } from '@/commons/constants'
import { localService } from '@/commons/services/localService'
import { moveService } from '@/commons/services/moveService'
import { globalOfficeService } from '@/commons/services/globalOfficeService'
import { AxiosResponse } from 'axios'
import { changeBlobToJson } from '@/store/apis/apiClient'

export enum FormItemType {
  Input = 'input',
  Select = 'select'
}

/**
 * マスタベースクラス
 *
 * @class
 */
@Component({
  name: 'MST_BASE'
})
export default class MST_BASE extends Vue {
  @Ref('inputFile')
  $inputFile!: HTMLInputElement

  /**
   * 画面ID
   */
  pageId = ''

  /**
   * 画面名
   */
  pageName = ''

  /**
   * 画面FORM
   */
  form = {}

  /**
   * テンプレートコード
   */
  templateCode = ''

  /**
   * 営業所
   */
  businessOfficeCache = ''

  /**
   * 営業所
   */
  businessOffice = ''

  /**
   * 営業所マスタリスト
   */
  businessOfficeMasterData: BusinessOfficeMasterData[] = []

  /**
   * 営業所マスタリスト
   */
  businessOfficeMasterDataCache: BusinessOfficeMasterData[] = []

  /**
   * 古いデータ
   */
  oldData: any

  /**
   * 変更したデータ
   */
  tableData: Array<any> = []

  /**
   * 当前表示データ
   */
  visableTableData: Array<any> = []

  /**
   * 削除したデータ
   */
  deleteTableData: Array<any> = []

  /**
   * チェックボックスで選択したデータ
   */
  multipleSelection: Array<any> = []

  /**
   * テーブル行で選択したデータ
   */
  singleSelection: any

  /**
   * ページロク
   */
  pageLock = false

  /**
   * 最大件数をチェックするか
   */
  checkMaxCount = false

  /**
   * CSVヘッダーリスト
   */
  csvHeaders: Array<any> = []

  /**
   * 最先のCSVヘッダー
   */
  firstCsvHeader = ''

  /**
   * 営業所選択機能なし
   */
  withoutBusinessSelector = false

  /**
   * 管理対象表示名
   *
   * @returns {string} 管理対象表示名
   */
  get managementTargetShowName(): string {
    return localService.userInfo?.managementTargetShowName
  }

  /**
   * 気に入り営業所
   *
   * @returns {string[]} 気に入り営業所
   */
  get favoriteOfficeIdList(): string[] {
    return common.favOfficeList
  }

  /**
   * 入力FORMのテンプレート
   */
  formTemplate: any = {}

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {}

  /**
   * マスタ画面ツールチップタイプ
   */
  tooltipType: Record<string, string> = {
    // 戻る
    back: 'back',
    // 営業所
    businessOffice: 'businessOffice',
    // 取消
    cancel: 'cancel',
    // 削除
    delete: 'delete',
    // 地図
    map: 'map',
    // テンプレート
    template: 'template',
    // 追加
    insert: 'insert',
    // 保存
    save: 'save',
    // コピー
    copy: 'copy',
    // 出力
    output: 'output',
    // 入力
    input: 'input'
  }

  /**
   * ページの表示件数
   */
  visablePageSize = 50

  /**
   * 当前ページ
   */
  currentPage = 1

  /**
   * 古いページ
   */
  oldPage = 1

  /**
   * FROMのREF
   */
  baseFormRef!: Form

  /**
   * テーブルのREF
   */
  baseTableRef!: Table

  /**
   * 第一列の項目（Focus用）
   */
  firstField = ''

  /**
   * 検索文字列
   */
  oldSearchText = ''

  /**
   * 検索文字列
   */
  searchText = ''

  /**
   * 検索目標
   */
  searchTargets: SearchTargets[] = []

  /**
   * 検索結果INDEX
   */
  searchResultIndexList: number[] = []

  /**
   * 検索結果のPoint
   */
  searchResultPoint = -1

  /**
   * 検索結果INDEX
   */
  searchResultIndex = -1

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('MST_BASE > mounted')
    this.initFormTemplate()
    moveService.beforeLogout = async (): Promise<void> => {
      this.doDestroy = () => {}
      moveService.beforePageChange = undefined
      await this.unlock()
    }

    moveService.beforePageChange = async (next: any): Promise<void> => {
      this.$log.debug('MST_BASE > beforePageChange')
      return await this.handMove(next, true)
    }

    globalOfficeService.beforeGlobalOfficeChange = async (next: any): Promise<void> => {
      this.$log.debug('MST_BASE > beforeGlobalOfficeChange')
      return await this.beforeGlobalOfficeChange(next)
    }

    this.handlePageWithoutBusinessSelector()
  }

  handlePageWithoutBusinessSelector(): void {
    const withoutList = masterConfig.notShowBusinessOfficeSelectorPageIdList
    if (indexOf(withoutList, this.pageId) > -1) {
      this.withoutBusinessSelector = true
    }
  }

  /**
   * 営業所マスタデータを取得する
   *
   * @param {string} dataType データタイプ
   * @returns {boolean} 変更できるかどうか
   */
  canCodeChange(dataType: string): boolean {
    return dataType === DataType.INSERT
  }

  /**
   * 定義ルールを初期化する
   *
   * @param {any} allRules 定義ルール
   * @returns {void}
   */
  initValidate(allRules: any): void {
    if (!allRules) {
      return
    }
    Object.values(allRules).forEach(rules => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      for (const rule of rules) {
        if (!rule.validRule) {
          continue
        }

        rule.validator = validateService.getValidator(rule.validRule)
      }
    })
  }

  clearValidate(): void {
    this.$nextTick(() => {
      this.baseFormRef.clearValidate()
    })
  }

  validateAll(): void {
    this.$nextTick(() => {
      this.baseFormRef.validate()
    })
  }

  /**
   * 営業所マスタデータを取得する
   *
   * @returns {void}
   */
  async fetchBusinessOfficeMaster(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > fetchBusinessOfficeMaster`)
    try {
      const param = {
        type: MasterType.SIMPLE
      }

      const masters = await getBusinessOfficeMasterData(param)
      this.businessOfficeMasterDataCache = (masters.list as BusinessOfficeMasterData[]) || []
      this.setBusinessOfficeMasterData(this.businessOfficeMasterDataCache)
    } catch (e) {
      this.$log.debug('GPSTrackerMaster > fetchBusinessOfficeMaster: e=', e)
      throw e
    }
  }

  @Watch('favoriteOfficeIdList', { immediate: true, deep: true })
  changeBusinessOfficeMasterData(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > favoriteOfficeIdList change`)
    if (!this.businessOfficeMasterDataCache) {
      return
    }

    this.setBusinessOfficeMasterData(this.businessOfficeMasterDataCache)
  }

  setBusinessOfficeMasterData(allList: BusinessOfficeMasterData[]): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > setBusinessOfficeMasterData`)
    if (this.withoutBusinessSelector) {
      this.businessOfficeMasterData = allList || []
      return
    }

    if (!allList) {
      this.businessOfficeMasterData = []
    } else {
      this.businessOfficeMasterData =
        allList.filter(item => {
          return indexOf(this.favoriteOfficeIdList, item.id) !== -1
        }) || []
    }

    if (this.businessOfficeMasterData.length === 0) {
      return
    }

    if (!this.businessOffice || findIndex(this.businessOfficeMasterData, { id: this.businessOffice }) === -1) {
      this.$log.debug(`MST_BASE > ${this.pageId} > init businessOffice`)
      this.businessOffice = this.businessOfficeMasterData[0].id as string
      this.businessOfficeCache = this.businessOffice
      this.onBusinessOfficeChange()
    } else {
      globalOfficeService.resetAllowGlobalOfficeChangeFlag()
    }
  }

  /**
   * 営業所を変更した時の処理
   *
   * @returns {void}
   */
  async onBusinessOfficeChange(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > changeBusinessOffice=`, this.businessOffice)
    if (!this.checkDataChanged()) {
      await this.handleBusinessOfficeChange()
      return
    }

    // 営業所選択ポップアップでいいえの場合
    if (globalOfficeService.allowGlobalOfficeChange) {
      await this.handleBusinessOfficeChange()
      globalOfficeService.resetAllowGlobalOfficeChangeFlag()
      return
    }

    const confirmCallback = async () => {
      try {
        if (!(await this.saveTableData())) {
          this.businessOffice = this.businessOfficeCache
          return
        }
      } catch (e) {
        this.businessOffice = this.businessOfficeCache
        return
      }

      this.handleBusinessOfficeChange()
    }

    const rejectCallback = () => {
      this.handleBusinessOfficeChange()
    }

    const cancelCallback = () => {
      this.businessOffice = this.businessOfficeCache
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback,
      cancelCallback
    )
  }

  async handleBusinessOfficeChange(): Promise<void> {
    this.unlock(this.businessOfficeCache)
    this.businessOfficeCache = this.businessOffice
    this.fetchTableData()
    this.pageLock = false
    this.checkPageLock()
  }

  /**
   * ページロック取得APIのパラメータを取得する
   *
   * @param {string} businessOffice 営業所
   * @returns {LockParam} パラメータ
   */
  getLockParam(businessOffice?: string): LockParam {
    const param = {
      pageId: this.pageId,
      businessOfficeId: businessOffice ? businessOffice : this.businessOffice
    } as LockParam

    return param
  }

  /**
   * ページロック状況を取得する
   *
   * @returns {void}
   */
  async checkPageLock(): Promise<void> {
    try {
      const params = this.getLockParam()

      const lockedUsers = (await checklock(params)).userIdList
      if (!lockedUsers || lockedUsers.length === 0) {
        return
      }

      if (lockedUsers[0] === localService.userInfo?.userId) {
        return
      }

      const confirmCallback = () => {
        this.pageLock = false
        lock(params)
      }

      const rejectCallback = () => {
        this.pageLock = true
      }

      msgBoxService.confirm(
        i18n.t('messages.masterCommon.pageLocked', [lockedUsers[0]]).toString(),
        confirmCallback,
        rejectCallback
      )
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > checkPageLock: e=`, e)
      throw e
    }
  }

  /**
   * ページロックを取消する
   *
   * @param {string} businessOffice 営業所
   * @returns {void}
   */
  async unlock(businessOffice?: string): Promise<void> {
    const params = this.getLockParam(businessOffice)
    await unlockApi(params)
  }

  /**
   * インスタンスが破棄された後にフック処理(VUEからextends)
   *
   * @returns {void}
   */
  destroyed(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > destroyed`)
    this.doDestroy()
    moveService.beforePageChange = undefined
    moveService.beforeLogout = (): Promise<void> => {
      return Promise.resolve()
    }
    globalOfficeService.beforeGlobalOfficeChange = undefined
  }

  /**
   * インスタンスが破棄された後にフック処理
   *
   * @returns {void}
   */
  doDestroy(): void {
    this.unlock()
  }

  /**
   * 検索前に、検索CALLBACKを戻る。
   *
   * @returns {any} 検索CALLBACK
   */
  protected fetchTableDataBefore(): () => any {
    return () => {}
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug(`MST_BASE > ${this.pageId} > fetchTableData`, this.businessOffice)
      const data = (await this.fetchTableDataBefore()()) as MasterData
      this.initTableData(data.list as Array<any>)
      await this.fetchTableDataAfter()
      return true
    } catch (e) {
      this.initTableData([])
      this.fetchTableDataAfter()
      this.$log.debug(`MST_BASE > ${this.pageId} > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * 検索後に実行したい操作。
   *
   * @returns {void}
   */
  fetchTableDataAfter(): void {}

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {Array<any>} data 検索されたデータ
   * @returns {void}
   */
  initTableData(data: Array<any>): void {
    if (!data) {
      data = []
    }
    this.oldData = cloneDeep(data)
    this.tableData = data
    this.deleteTableData = []
    this.initVisableData()
    this.initSearchBox()
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @returns {void}
   */
  initVisableData(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > initVisableData`)
    this.currentPage = 1
    this.oldPage = 1
    this.setVisableTableData()
  }

  /**
   * 検索ボックスの初期化を実行する。
   *
   * @returns {void}
   */
  initSearchBox(): void {
    this.oldSearchText = ''
    this.searchText = ''
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {number} currentPage 当前ページ
   * @returns {void}
   */
  setVisableTableData(currentPage = 1): void {
    if (!this.tableData) {
      return
    }

    const startIndex = (currentPage - 1) * this.visablePageSize
    const endIndex = currentPage * this.visablePageSize

    this.visableTableData = this.tableData.slice(startIndex, endIndex)
    this.form = {
      tableData: this.visableTableData
    }
  }

  /**
   * FORMのREFを渡す
   *
   * @param {Form} ref 当前ページ
   * @returns {void}
   */
  initFormRef(ref: Form): void {
    this.baseFormRef = ref
  }

  /**
   * TABLEのREFを渡す
   *
   * @param {Table} ref 当前ページ
   * @returns {void}
   */
  initTableRef(ref: Table): void {
    this.baseTableRef = ref
  }

  handleSortChange(): void {
    if (this.checkDataChanged()) {
      msgBoxService.info(i18n.t('messages.masterCommon.needSave').toString())
      this.baseTableRef.clearSort()
    }
  }

  /**
   * 当前ページを変更するときの処理
   *
   * @param {number} page 当前ページ
   * @param {boolean} isChangedBySearchBox 検索ボックスで改ページか
   * @returns {void}
   */
  onCurrentPageChange(page: number, isChangedBySearchBox: boolean): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCurrentPageChange > currentPage=`, page)

    if (!isChangedBySearchBox) {
      this.initSearchBox()
    }

    // 変更があるかどうかを判断する
    if (this.checkDataChanged()) {
      this.currentPage = this.oldPage
      msgBoxService.info(i18n.t('messages.masterCommon.needSave').toString())
      return
    }

    this.baseFormRef.validate(valid => {
      if (!valid) {
        this.currentPage = this.oldPage
        msgBoxService.info(i18n.t('messages.masterCommon.needSave').toString())
        return
      }

      this.oldPage = this.currentPage
      this.setVisableTableData(page)
    })
  }

  /**
   * チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async dataValidate(): Promise<boolean> {
    try {
      const valid = await this.baseFormRef.validate()
      return valid
    } catch {
      this.baseFormRef.validate((valid, fields) => {
        if (!valid) {
          this.focusOnValidField(fields)
          return
        }
      })

      return false
    }
  }

  /**
   * エラー項目をFOCUS
   *
   * @param {any} fields エラー項目
   * @returns {void}
   */
  focusOnValidField(fields: any): void {
    if (!fields) {
      return
    }

    const keys = Object.keys(fields)
    if (!keys || keys.length === 0) {
      return
    }

    const target = document.getElementById(`${keys[0]}`)
    target?.focus()
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @returns {void}
   */
  onSearchBoxClick(): void {
    this.searchText = this.searchText.replace(/[,]/g, '')

    if (!this.searchText) {
      this.initSearchBox()
      return
    }

    // 変更があるかどうかを判断する
    if (this.checkDataChanged()) {
      msgBoxService.info(i18n.t('messages.masterCommon.needSave').toString())
      return
    }

    if (this.oldSearchText === this.searchText) {
      this.doSearchBoxHighlight()
      return
    }

    // 命中したデータリストを作成する
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
    this.oldSearchText = ''
    this.searchTargetIndexByTextBox(this.tableData)
    if (this.searchResultIndexList.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noSearchBoxResult').toString())
      return
    }

    this.oldSearchText = this.searchText
    this.doSearchBoxHighlight()
  }

  /**
   * 検索結果のHighlight
   *
   * @returns {void}
   */
  doSearchBoxHighlight(): void {
    this.toNextSearchResultPoint()
    this.handleSearchResultHighlight()
    this.scrollToHighlightRow()
  }

  /**
   * 次の検索結果へ
   *
   * @returns {void}
   */
  toNextSearchResultPoint(): void {
    const targetsLength = this.searchResultIndexList.length
    if (this.searchResultPoint === targetsLength - 1) {
      this.searchResultPoint = 0
    } else {
      this.searchResultPoint++
    }
  }

  /**
   * 検索結果のページと位置を確定
   *
   * @returns {void}
   */
  handleSearchResultHighlight(): void {
    if (this.searchResultIndexList.length === 0) {
      return
    }

    const targetindex = this.searchResultIndexList[this.searchResultPoint]
    this.$log.debug(`MST_BASE > ${this.pageId} > handleSearchResultHighlight > targetindex=`, targetindex)
    // 目標のページを算出する
    this.searchResultIndex = targetindex % this.visablePageSize
    const targetPage = Math.ceil((targetindex + 1) / this.visablePageSize) || 1
    if (this.currentPage !== targetPage) {
      this.currentPage = targetPage
      this.onCurrentPageChange(this.currentPage, true)
    }
    this.$log.debug(
      `MST_BASE > ${this.pageId} > handleSearchResultHighlight > searchResultIndex=`,
      this.searchResultIndex
    )
  }

  scrollToHighlightRow(): void {
    this.$nextTick(() => {
      const targetId = `tableData[${this.searchResultIndex}].${this.firstField}`
      const target = document.getElementById(targetId)
      target?.scrollIntoView()
    })
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @param {Array<any>} targetTableData 対象データ
   * @returns {void}
   */
  searchTargetIndexByTextBox(targetTableData: Array<any>): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > searchTargetIndexByTextBox > searchText=`, this.searchText)
    targetTableData.forEach((item, index) => {
      const target: any[] = []
      this.searchTargets.forEach(searchTargets => {
        const { type, field, list, getData } = searchTargets
        if (type === FormItemType.Input) {
          target.push(item[field])
        } else if (list) {
          const generalList = list()
          const format = {
            code: 'code',
            value: 'name'
          }

          target.push(HelperService.getDefinitionName(generalList, item[field], format))
        } else if (getData) {
          target.push(getData(item))
        }
      })

      const itemContext = target.toString()

      if (itemContext.includes(this.searchText)) {
        this.$log.debug(`MST_BASE > ${this.pageId} > itemContext.includes > itemContext=`, itemContext)
        this.searchResultIndexList.push(index)
      }
    })
  }

  /**
   * テーブル行CLASS（検索用、rewriteの場合、必ずこの機能を流用してください。）
   *
   * @param {any} rowData 対象データ
   * @returns {string} 目標CLASS
   */
  tableRowClassName(rowData: any): string {
    const { rowIndex } = rowData
    if (this.searchResultIndex === -1) {
      return ''
    }

    if (this.searchResultIndex === rowIndex) {
      return 'search-result-row'
    }

    return ''
  }

  /**
   * テーブルの選択したデータを変更する。
   *
   * @param {any} row 選択されたデータ
   */
  handleCurrentChange(row: any): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > handleCurrentChange`, row)
    this.singleSelection = row
  }

  /**
   * テーブルのチェックしたデータを変更する
   *
   * @param {Array<any>} val チェックされたデータリスト
   * @returns {void}
   */
  handleSelectionChange(val: Array<any>): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > handleSelectionChange`, val)
    this.multipleSelection = val
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCancelButtonClick`)
    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.tableData = cloneDeep(this.oldData)
      this.initVisableData()
      this.initSearchBox()
      this.deleteTableData = []
      this.fetchTableDataAfter()
      this.$nextTick(() => {
        this.clearValidate()
      })
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onBackButtonClick`)
    const doBack = () => {
      moveService.beforePageChange = undefined
      this.doBack()
    }

    this.handMove(doBack)
  }

  /**
   * ヘッダ上の営業所選択をクリックした時の処理。
   *
   * @param {any} next 次の処理
   * @returns {void}
   */
  beforeGlobalOfficeChange(next: any): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > beforeGlobalOfficeChange`)
    if (!next) {
      return
    }

    if (!this.checkDataChanged()) {
      next()
      return
    }

    const confirmCallback = async () => {
      try {
        await this.saveTableData()
        next()
      } catch (e) {
        this.$log.debug(`MST_BASE > ${this.pageId} > onBackButtonClick`, e)
        throw e
      }
    }

    const rejectCallback = () => {
      globalOfficeService.allowGlobalOfficeChange = true
      next()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  /**
   * 遷移する時の処理。
   *
   * @param {any} next 遷移処理
   * @param {boolean} needReset 保存失敗またはキャンセル時に、Routeをリセットするかどうか
   * @returns {void}
   */
  handMove(next: any, needReset = false): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > handMove`)
    if (!next) {
      return
    }

    if (!this.checkDataChanged()) {
      next()
      return
    }

    const confirmCallback = async () => {
      try {
        this.saveTableDataAfter = async () => {
          msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString(), next)
        }

        if (!(await this.saveTableData()) && needReset) {
          this.$router.forward()
        }
      } catch (e) {
        this.$log.debug(`MST_BASE > ${this.pageId} > onBackButtonClick`, e)
        if (needReset) {
          this.$router.forward()
        }
        throw e
      }
    }

    const rejectCallback = () => {
      next()
    }

    const cancelCallback = () => {
      if (!needReset) {
        return
      }

      this.$router.forward()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback,
      cancelCallback
    )
  }

  /**
   * 前画面へ戻る
   *
   * @returns {void}
   */
  doBack(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > doBack`)
    this.$router.back()
  }

  /**
   * 項目キーで項目キーリストを取得する
   *
   * @param {string} key 項目名
   * @returns {string[]} 項目名リスト
   */
  getValidateFields(key: string): string[] {
    this.$log.debug(`MST_BASE > ${this.pageId} > getValidateFields, key=`, key)
    if (!this.tableData || this.tableData.length === 0) {
      return []
    }

    const fields = this.tableData.map((item, index) => {
      return `tableData[${index}].${key}`
    })

    return fields
  }

  /**
   * 指定する項目をチェックする。
   *
   * @param {Form} formRef フォーム
   * @param {string[]} fields 項目名リスト
   * @returns {void}
   */
  validateField(formRef: Form, fields: string[]): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > validateField, fields=`, fields)
    if (!formRef || !fields || fields.length === 0) {
      return
    }

    formRef.validateField(fields)
  }

  /**
   * 指定する項目をチェックする。
   *
   * @param {Form} formRef フォーム
   * @param {string} field 項目名
   * @returns {void}
   */
  locationRevalidate(formRef: Form, field: string): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > locationRevalidate, field=`, field)
    if (!this.singleSelection || !formRef || !field) {
      return
    }

    const index = findIndex(this.tableData, this.singleSelection)
    if (index === -1) {
      return
    }

    formRef.validateField(`tableData[${index}].${field}`)
  }

  /**
   * 重複チェックある項目を変更した後に実行する。
   *
   * @param {Form} formRef フォーム
   * @param {string} key 項目名
   * @returns {void}
   */
  onRepeatItemChange(formRef: Form, key: string): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onRepeatItemChange`)
    const fields = this.getValidateFields(key)
    this.validateField(formRef, fields)
  }

  /**
   * 8ビット未満自動補正0
   *
   * @param {string} val コード
   * @returns {string} val
   */
  prefixZero(val: string): string {
    while (val.length < 8 && val.length > 0) {
      val = '0' + val
    }
    return val
  }

  /**
   * 削除前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  deleteTableDataBefore(): boolean {
    return true
  }

  /**
   * 削除後に行う。
   *
   */
  deleteTableDataAfter(): void {
    this.clearValidate()
  }

  /**
   * 削除ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  async onDeleteButtonClick(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onDeleteButtonClick`)
    if (!this.multipleSelection || this.multipleSelection.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataSelected').toString())
      return
    }

    if (!this.deleteTableDataBefore()) {
      this.$log.debug(`MST_BASE > ${this.pageId} > can't delete`)
      return
    }

    const confirmCallback = () => {
      this.doDelete()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.deleteConfirm').toString(), confirmCallback)
  }

  /**
   * 削除処理。
   *
   * @returns {void}
   */
  doDelete() {
    this.multipleSelection.forEach(item => {
      const index = findIndex(this.tableData, item)
      if (index !== -1) {
        const visableTableDataIndex = findIndex(this.visableTableData, item)
        this.visableTableData.splice(visableTableDataIndex, 1)
        const target = this.tableData.splice(index, 1)

        if (target[0].dataType === DataType.INSERT) {
          return
        }
        this.deleteTableData.push(target[0])
      }
    })
    this.deleteTableDataAfter()
  }

  /**
   * テンプレートボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onTamplateButtonClick(): Promise<void> {
    const param = {
      templateCode: this.templateCode
    }

    const res = await templateDownload(param)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * 追加ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onInsertButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onInsertButtonClick`)
    if (!this.checkInsert()) {
      return
    }

    const item = {
      ...this.formTemplate,
      dataType: DataType.INSERT,
      index: this.tableData.length
    }

    this.tableData.push(item)
    this.visableTableData.push(item)
    this.clearValidate()
    this.insertAfter()
  }

  insertAfter(): void {
    this.baseTableRef.clearSort()
    this.$nextTick(() => {
      const targetId = `tableData[${this.visableTableData.length - 1}].${this.firstField}`
      const target = document.getElementById(targetId)
      target?.focus()
    })
  }

  /**
   * 登録できる件数をチェックする
   *
   * @returns {boolean} true
   */
  checkInsert(): boolean {
    if (!this.checkMaxCount) {
      return true
    }

    if (this.tableData.length >= MaxDataCount) {
      msgBoxService.info(i18n.t('messages.masterCommon.maxCount', [this.pageName, MaxDataCount]).toString())
      return false
    }

    return true
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onSaveButtonClick(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onSaveButtonClick`)
    try {
      await this.saveTableData()
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > onSaveButtonClick: e=`, e)
      throw e
    }
  }

  /**
   * 保存前に、テーブルチェックを行う。
   *
   * @returns {any} チェック結果
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected saveTableDataBefore(): any {
    return true
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  saveTableDataAPI(): any {}

  /**
   * 保存後に実行したい操作。
   *
   * @returns {void}
   */
  async saveTableDataAfter(): Promise<void> {
    msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString())
    await this.fetchTableData()
  }

  /**
   * エラー時の処理
   *
   * @returns {void}
   * @param {any} e エラーメッセージ
   */
  onSaveTableDataError(e: any): void {
    const err = e as AxiosResponse<ResponseData>
    changeBlobToJson(err).then(commonResponse => {
      this.handlerSaveTableError(commonResponse)
    })
  }

  handlerSaveTableError(commonResponse: CommonResponse) {
    if (commonResponse.common.code === 'E00093') {
      this.fetchTableData()
    }
  }

  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * 保存パラメータを作成する
   *
   * @returns {any} 保存パラメータ
   */
  getUpsertRequest(): any {
    const param = {
      businessOfficeId: this.businessOfficeCache,
      addDataList: [] as Array<any>,
      updateDataList: [] as Array<any>,
      deleteDataList: this.deleteTableData.map(item => item.id)
    }

    this.tableData.forEach(item => {
      if (item.dataType === DataType.INSERT) {
        param.addDataList.push(item)
        return
      }

      ;(this.oldData as Array<any>).forEach(olditem => {
        if (olditem.id !== item.id) {
          return
        }

        if (findIndex(param.updateDataList, item) !== -1) {
          return
        }

        if (!HelperService.isObjectDataChanged(olditem, item)) {
          return
        }

        param.updateDataList.push(item)
      })
    })

    return param
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {Promise<boolean>} 保存結果
   */
  async saveTableData(): Promise<boolean> {
    try {
      this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData`)
      if (!this.checkDataChanged()) {
        msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
        return false
      }

      if (!(await this.saveTableDataBefore())) {
        this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData > vaildation error`)
        return false
      }

      await this.saveTableDataAPI()
      await this.saveTableDataAfter()

      return true
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData: e=`, e)
      this.onSaveTableDataError(e)
      throw e
    }
  }

  /**
   * moreボタンをクリックした時の処理。
   *
   * @param {string} command コマンド
   * @returns {void}
   */
  handleCommand(command: string): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > handleCommand: command=`, command)
    switch (command) {
      case 'copy':
        this.onCopyButtonClick()
        break
      case 'input':
        this.onInputButtonClick()
        break
      case 'output':
        this.onOutputButtonClick()
        break
      default:
        break
    }
  }

  /**
   * コピーボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCopyButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCopyButtonClick`)
    if (!this.multipleSelection || this.multipleSelection.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataSelected').toString())
      return
    }

    if (!this.tableData) {
      return
    }

    if (this.checkMaxCount) {
      if (this.tableData.length + this.multipleSelection.length > MaxDataCount) {
        msgBoxService.info(i18n.t('messages.masterCommon.maxCount', [this.pageName, MaxDataCount]).toString())
        return
      }
    }

    const multipleSelection = this.multipleSelection.map((item, index) => {
      return {
        ...item,
        id: '',
        dataType: DataType.INSERT,
        index: this.tableData.length + index
      }
    })
    this.beforeDataCopy(multipleSelection)

    const multipleSelectionClone = cloneDeep(multipleSelection)
    this.tableData.push(...multipleSelectionClone)
    this.visableTableData.push(...multipleSelectionClone)
    this.clearValidate()
    this.insertAfter()
  }

  /**
   * コピー前の処理。
   *
   * @param {Array<any>} multipleSelection コピー対象
   * @returns {void}
   */
  // eslint-disable-next-line
  beforeDataCopy(multipleSelection: Array<any>): void {}

  /**
   * 入力ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  async onInputButtonClick(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onInputButtonClick`)
    if (!this.checkDataChanged()) {
      this.handleInputFile()
      return
    }

    const confirmCallback = async () => {
      try {
        if (!(await this.saveTableData())) {
          return
        }
      } catch (e) {
        return
      }

      this.handleInputFile()
    }

    const rejectCallback = () => {
      this.handleInputFile()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  /**
   * 入力ファイル処理
   *
   * @returns {void}
   */
  handleInputFile(): void {
    this.$inputFile.value = ''
    this.$inputFile.click()
  }

  /**
   * 選択したファイルを変更した時の処理。
   *
   * @param {any} event イベント
   * @returns {void}
   */
  onFileChange(event: any): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onFileChange: event=`, event)
    if (event.target.files.length === 0) {
      return
    }

    const file = event.target.files[0]

    if (!file) {
      return
    }
    const fileName = file.name

    if (!this.inputFileCheck(file)) {
      return
    }

    const parseCallback = (result: any) => {
      this.afterFileParsed(fileName, result)
    }

    const errorCallback = () => {
      msgBoxService.info(i18n.t('vaildate.csvFile.parseError').toString())
    }

    try {
      CSVService.parseCsv(file, this.csvHeaders, parseCallback, errorCallback)
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > onFileChange: e=`, e)
      // ファイルの解析に失敗しました。
      msgBoxService.info(i18n.t('vaildate.csvFile.parseError').toString())
    }
  }

  /**
   * 入力ファイルをチェックする
   *
   * @param {File} file file
   * @returns {boolean} チェック結果
   */
  inputFileCheck(file: File): boolean {
    const fileName = file.name
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const fileExtension = fileName.split('.').pop().toLowerCase()
    if (fileExtension !== 'csv') {
      msgBoxService.info(i18n.t('vaildate.csvFile.csvMust').toString())
      return false
    }

    if (file.size > MaxFileSize * 1024 * 1024) {
      msgBoxService.info(i18n.t('vaildate.csvFile.maxSize', [MaxFileSize]).toString())
      return false
    }

    return true
  }

  /**
   * CSVファイルをパースした時の処理。
   *
   * @param {string} fileName ファイル名
   * @param {any[]} result データ
   * @returns {void}
   */
  afterFileParsed(fileName: string, result: any[]): void {
    if (!result || result.length === 0) {
      msgBoxService.info(i18n.t('vaildate.csvFile.noData', [fileName]).toString())
      return
    }

    const firstRowCol = result[0][this.csvHeaders[0]]
    let target = []
    let indexOffset = 1
    if (this.firstCsvHeader && this.firstCsvHeader === firstRowCol) {
      target = result.slice(1, result.length)
      indexOffset = 2
    } else {
      target = result
    }

    this.handleCsvInputData(target, indexOffset)
  }

  /**
   * CSVデータを処理する。
   *
   * @param {any[]} dataList データ
   * @param {number} indexOffset 開始行のoffset
   * @returns {void}
   */
  handleCsvInputData(dataList: any[], indexOffset: number): void {
    const rule = this.getCsvRule()
    const { messageList, validData } = validateService.csvValidate(dataList, rule, this.tableData, indexOffset)
    const validDataLength = validData.length
    let msgString = ''

    if (messageList && messageList.length > 0) {
      msgString = HelperService.changeMessageListToString(messageList)
    }

    if (!validData || validDataLength === 0) {
      if (isEmpty(msgString)) {
        msgBoxService.info(i18n.t('messages.masterCommon.fileInserted', [0]).toString())
      } else {
        msgBoxService.info(msgString, undefined, { width: '80vw' } as MessageBoxOption)
      }
      return
    }

    // 一覧件数+登録件数が1000件を超える場合、ダイアログを表示する。
    if (this.checkMaxCount) {
      if (validDataLength + this.tableData.length > MaxDataCount) {
        msgBoxService.info(i18n.t('messages.masterCommon.maxCount', [this.pageName, MaxDataCount]).toString())
        return
      }
    }

    this.csvDataMergeIntoTableDataBefore(validData)
    this.tableData.splice(this.tableData.length, 0, ...validData)
    this.visableTableData.splice(this.visableTableData.length, 0, ...validData)
    this.baseTableRef.clearSort()

    const option = {
      onClose: (): void => {
        if (messageList && messageList.length > 0) {
          msgBoxService.info(msgString, undefined, { width: '80vw' } as MessageBoxOption)
        }
      }
    } as MessageBoxOption

    msgBoxService.info(i18n.t('messages.masterCommon.fileInserted', [validDataLength]).toString(), undefined, option)
  }

  /**
   * CSVデータ合併前の処理
   *
   * @param {any[]} dataList データ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  csvDataMergeIntoTableDataBefore(dataList: any[]): void {
    dataList.forEach(item => {
      item.dataType = DataType.INSERT
    })
  }

  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * CSVファイルのチェックツールを取得する
   *
   * @returns {any} チェックツール
   */
  protected getCsvRule(): any {}

  /**
   * 出力
   *
   * @returns {void}
   */
  async doTableDataOutput(): Promise<void> {
    if (!this.oldData || this.oldData.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataToExport').toString())
      return
    }

    await this.tableDataOutputAPI()
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  tableDataOutputAPI(): void {}

  /**
   * 出力ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  async onOutputButtonClick(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onOutputButtonClick`)
    if (!this.checkDataChanged()) {
      await this.doTableDataOutput()
      return
    }

    const confirmCallback = async () => {
      try {
        if (!(await this.saveTableData())) {
          return
        }
      } catch (e) {
        return
      }

      this.doTableDataOutput()
    }

    const rejectCallback = async () => {
      await this.doTableDataOutput()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback
    )
  }

  /**
   * リストが変更されましたかをチェックする。
   *
   * @returns {boolean} isChanged
   */
  checkDataChanged(): boolean {
    return HelperService.isTableDataChanged(this.oldData, this.tableData as [])
  }

  /**
   * 画面ツールチップを取得する
   *
   * @param {string} key ツールチップキー
   * @returns {any} ツールチップ
   */
  getTooltip(key: string): any {
    return {
      content: i18n.t(`master.tooltipCommon.${key}`),
      placement: 'bottom-start',
      delay: { show: 1000, hide: 0 }
    }
  }

  /**
   * 営業所コードの表示名を取得する
   *
   * @param {BusinessOfficeMasterData} row 営業所データ
   * @returns {string} 表示名
   */
  getBusinessOfficeLabel(row: BusinessOfficeMasterData): string {
    return `${row.businessOfficeName}(${row.businessOfficeCode})`
  }
}
</script>
